import { Spinner } from "@/elements"
import { graphql } from "@/gql"
import {
  MyJobsActiveJobPostsClientQueryDocument,
  MyJobsActiveJobPostsCreativeQueryDocument,
  Ordering,
} from "@/gql/graphql"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import type { NovaSession } from "@/types"
import { useQuery } from "@apollo/client"
import { useRef } from "react"
import { MyJobPostsItem } from "./MyJobPostsItem"
import { MyJobPostsItemSkeleton } from "./MyJobPostsItemSkeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ClipboardXIcon } from "@/icons/outline/ClipboardXIcon"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

graphql(`
  query MyJobsActiveJobPostsCreativeQuery($first: Int, $after: String, $creativeId: ID!, $order: JobPostingOrder) {
    creative(id: $creativeId) {
      __typename
      id
      jobPostings(filters: { status: { exact: PUBLISHED } }, first: $first, after: $after, order: $order) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          cursor
          node {
            id
            completionStatus {
              id
              currentStep
            }
            ...MyJobPostsItem
          }
        }
      }
    }
  }
`)
graphql(`
  query MyJobsActiveJobPostsClientQuery($first: Int, $after: String, $clientId: ID!, $order: JobPostingOrder) {
    client(id: $clientId) {
      __typename
      id
      jobPostings(filters: { status: { exact: PUBLISHED } }, first: $first, after: $after, order: $order) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          cursor
          node {
            id
            completionStatus {
              id
              currentStep
            }
            ...MyJobPostsItem
          }
        }
      }
    }
  }
`)

interface Props {
  currentUserField: "creative" | "client"
  serverSession: NovaSession
}
const PAGE_SIZE = 6
export const ActiveJobsList: React.FC<Props> = ({ currentUserField, serverSession }) => {
  const {
    data: creativeData,
    loading: creativeLoading,
    fetchMore: creativeFetchMore,
  } = useQuery(MyJobsActiveJobPostsCreativeQueryDocument, {
    variables: {
      creativeId: `${serverSession.user.creative.id}`,
      first: PAGE_SIZE,
      after: null,
      order: {
        publishedAt: Ordering.Desc,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    skip: currentUserField === "client",
    context: {
      version: 2,
    },
  })

  const {
    data: clientData,
    loading: clientLoading,
    networkStatus,
    fetchMore: clientFetchMore,
  } = useQuery(MyJobsActiveJobPostsClientQueryDocument, {
    variables: {
      clientId: `${serverSession.user.client.id}`,
      first: PAGE_SIZE,
      after: null,
      order: {
        publishedAt: Ordering.Desc,
      },
    },

    skip: currentUserField === "creative",
    context: {
      version: 2,
    },
  })

  const jobPostings = creativeData?.creative.jobPostings || clientData?.client.jobPostings
  const loading = currentUserField === "creative" ? creativeLoading : clientLoading
  const fetchMore = currentUserField === "creative" ? creativeFetchMore : clientFetchMore

  const edges = jobPostings?.edges || []

  const loadMoreRef = useRef(null)
  useHandleListEndReached({
    pageInfo: jobPostings?.pageInfo,
    first: PAGE_SIZE,
    fetchMore,
    networkStatus,
    observerRef: loadMoreRef,
  })

  return (
    <div className="relative flex flex-col gap-y-5 px-5 sm:px-10 md:gap-y-0 md:px-6">
      {loading && edges.length === 0 ? (
        new Array(4).fill(0).map((_, index) => (
          <div key={index}>
            <MyJobPostsItemSkeleton />
            {index !== 3 && <div className="mx-auto mt-[-1px] hidden h-[1px] w-[calc(100%-48px)] bg-bg-3 md:block" />}
          </div>
        ))
      ) : edges.length > 0 ? (
        edges.map((edge, index) => {
          const node = edge?.node
          if (!node) return null
          return (
            <div key={node.id} className="group">
              <MyJobPostsItem
                jobPosting={node}
                eagerImage={index <= 4}
                serverSession={serverSession}
                onClick={() => {
                  ampli.activeJobCardOpen({
                    status: node?.completionStatus?.currentStep || "",
                    job_id: decodeGraphqlNodeId(node.id).numericalId || "",
                  })
                }}
              />
              {index !== edges.length - 1 && (
                <div className="mx-auto mt-[-1px] hidden h-[1px] w-[calc(100%-48px)] bg-bg-3 group-hover:bg-bg-2 md:block" />
              )}
            </div>
          )
        })
      ) : (
        <div className="mx-auto w-[calc(100%-48px)]">
          <EmptyState title="No active jobs" description="You don't have any active jobs." Icon={ClipboardXIcon} />
        </div>
      )}
      <div ref={loadMoreRef} className="relative mb-[50vh] mt-[-50vh] h-[1px] w-full" />
      <div className="relative mt-4 flex h-[40px] w-full shrink-0 flex-row items-center justify-center">
        {jobPostings?.pageInfo?.hasNextPage && <Spinner className="bg-bg-5" size="medium" />}
      </div>
    </div>
  )
}
